// stroke.* — uniform pub/sub drawing hot path.
//
// (protocol.md §4 / protocol-events.md §1)
//
// Three messages per stroke: `stroke.start` (metadata only), one or more
// `stroke.segment` (point lists), `stroke.end` (terminator). The server
// stamps `uid` / `seq` / `ts` and rebroadcasts the same shape to every
// connection.
import { z } from 'zod';
import { MAX_DISPLAY_NAME_LENGTH, MAX_SEGMENT_POINTS, MAX_STROKE_WIDTH, MIN_DISPLAY_NAME_LENGTH, MIN_STROKE_WIDTH, } from '../constants.js';
import { strokePointSchema } from '../shared/point.js';
/**
 * Pen kinds available in MVP (consult 2026-05-09 / architecture.md §19.3).
 *
 * - `brush`   : メインブラシ (alpha 値で不透明線画から build-up 半透明まで連続化、
 *               per-stamp alpha = 累乗カーブで連続化、segment ごとに即 commit)
 * - `flat`    : uniform alpha 塗り (= max 蓄積モード、stroke 終端で 1 回 commit、
 *               マスク・逆マスクの基盤)
 * - `marker`  : マーカー (= max 蓄積モード + per-stamp alpha 線形カーブ +
 *               太さ・濃さ筆圧連動 default ON、コピックマーカー風の独自表現).
 *               consult 2026-05-09 で `'flat'` の濃さ筆圧 toggle を disabled 化
 *               した代わりに「max 蓄積 + 濃さ筆圧連動」の組合せを別 pen として
 *               提供. 蓄積モード `'max'` は `'flat'` と同じ、commit タイミングも
 *               stroke 終端 1 回で同じ. per-stamp alpha は線形 (`stroke.alpha *
 *               pAlphaPressure`、累乗カーブなし) で `'brush'` の累乗カーブとは
 *               区別. 独自性: max 蓄積 + per-stamp + 筆圧連動の組合せは
 *               CSP / Photoshop に直接対応がない (試験実装、α/β リリース判断は
 *               実機検証後)
 * - `eraser`  : 消しゴム (color/alpha ignored at render time)
 * - `texture` : テクスチャブラシ (= プリセット alpha mask を既存ペンパターンに
 *               alpha multiply する新 kernel、architecture.md §19.3 /
 *               texture-brush-design.md). β (Phase 10) の目玉ペン. spike では
 *               preset 選択フィールドを持たず render 側で「紙」(world mapping)
 *               をハードコードする (preset/penParams field は横展開フェーズで
 *               確定、texture-brush-design.md §4 / §9).
 * - `tone`    : スクリーントーン (= 閾値ディザによる網点。texture-brush-design.md
 *               §12.2 / consult 2026-06-20 段階1). texture とは**別カテゴリ**の
 *               独立 pen — texture は alpha mask の multiply だが tone は閾値
 *               ディザで網点を生成する別機構 (dither kernel は drawing-engine
 *               担当). 密度は `penParams.density` の連続番手 (0..1 スカラ) で
 *               指定し、旧 static 網点 preset (`tone-dot-*`) を置換する.
 * - `airbrush`: エアブラシ (= β Phase 10 の目玉ペン、texture と対になる2本目。
 *               airbrush-design-2026-08-15.md). tone と同じ**閾値 seam**に相乗り
 *               するが、閾値マップをテーブル引きではなく **ステートレスハッシュ
 *               `hash(seed, pointIndex, i, nx>>grain, ny>>grain)`** でその場計算
 *               する (§1/§2)。ステートフル PRNG ではなくハッシュを選んだのは、
 *               同時描画で複数ストロークがインターリーブしても結果が
 *               `(seed, pointIndex, i, nx, ny)` の純関数のまま保たれ、到着順や
 *               他人のストロークが絵に影響しない構造的な免疫を得るため (§1.1)。
 *               `seed` はストローク単位で client 生成、server は再生成しない
 *               (§4-4)。kernel 自体は drawing-core 側の後続フェーズで実装。
 *
 * 旧 4 ペン (`'normal' | 'water' | 'alpha' | 'eraser'`) からの統合 (consult
 * 2026-05-09): `'normal'` + `'water'` を `'brush'` に統合し alpha 値で挙動を
 * 連続化、`'alpha'` を `'flat'` に rename. CSP / Photoshop 流の「ペン種は効果軸の
 * 組合せで表現、固定ロジックを持たない」設計思想に整合.
 *
 * Future pens (`blur` / `oil` / `realwater` later) will extend this enum
 * without a protocol version bump. (protocol-events.md §1)
 *
 * - `crayon`  : クレヨン (Phase 11 軽量ペン, phase11-light-pens-2026-08-21.md
 *               §2). `tone` と同じ**閾値 seam**に相乗り (`textureThreshold:
 *               true`) するが、閾値マップは連続番手ではなく静的な粗い蝋目
 *               world マップ. 静的マップは同じ texel に何度スタンプしても同じ
 *               判定を返すため、谷 (= 高閾値 texel) は原理的に埋まらない
 *               (= 「かすれ維持」がクレヨンの本質、§2.1)。密度レベル (§2.4) は
 *               ストローク単位の定数として `stroke.alpha` から導出する
 *               (フォールバック採用、詳細は `createCrayonStampRenderer` の doc)。
 * - `pastel`  : パステル (Phase 11 軽量ペン, 同 §2). texture プリセットと同じ
 *               **alpha multiply** seam に相乗り. マップは細かい粉目 world
 *               マップ. alpha multiply は谷の texel にも僅かな alpha が乗るため
 *               重ね塗りで埋まる (= ベタ塗りにも使える、§2.1/§2.3)。
 * - `dot`     : ドット (Phase 11 軽量ペン, 同 §3). 新 kernel はゼロ —
 *               `StampRenderer` の実装を包むデコレータが `line()` の掃引を
 *               spacing ごとに間引いて内側 renderer の `dot()` だけを呼ぶ
 *               (`stamp/dotDecorator.ts`)。置かれるスタンプ自体は普通のブラシ
 *               スタンプなので、Wasm ABI の pen kind は `brush` (0) を共有する
 *               (`penKindFromName`)。
 */
export const strokePenSchema = z.enum([
    'brush',
    'flat',
    'marker',
    'eraser',
    'texture',
    'tone',
    'airbrush',
    'crayon',
    'pastel',
    'dot',
]);
/**
 * Texture brush preset ids (texture-brush-design.md §1 / §11 step 1).
 *
 * These are internal ids carried on the wire; UI display names ("紙" / "毛先" /
 * etc.) live on the web side. Each preset also defines a `mapping` mode
 * (`world` / `stamp`) on the render side — the protocol only carries the id.
 *
 * - `paper`   : 紙   (world mapping). spike default — `pen='texture'` without
 *               `penParams` renders as `paper` (texture-brush-design.md §4).
 * - `bristle` : 毛先 (stamp mapping, rotate-with-stroke)
 * - `spray`   : スプレー (stamp mapping, world 候補は実機調整 §1). **静的
 *               texture では型紙止まりのため動的散布は `pen='airbrush'` へ
 *               移管済** (airbrush-design-2026-08-15.md §0)。airbrush 投入に
 *               伴い enum からの撤去も検討対象だが、`drawing-core` 側の残置
 *               static spray 実装の整理は回帰範囲が別軸のため別途 (今回は
 *               enum に残置したまま UI 非露出を維持).
 * - `cloud`   : 雲   (stamp mapping)
 * - `hatch`   : カケアミ (world mapping)
 *
 * Screentone (網点) は texture preset ではなく独立 pen `'tone'` に昇格した
 * (texture-brush-design.md §12.2 / consult 2026-06-20 段階1). 旧 static 網点
 * preset (`tone-dot-light/mid/dark`) は撤去され、密度は `penParams.density` の
 * 連続番手で指定する閾値ディザに置換された (texture の alpha-multiply とは別機構).
 */
export const texturePresetSchema = z.enum(['paper', 'bristle', 'spray', 'cloud', 'hatch']);
/**
 * Pen-specific parameters carried on `stroke.start` (texture-brush-design.md §4
 * 案B / §11 step 1). Discriminated union keyed on `kind` so future pens can be
 * added without a protocol version bump.
 *
 * - `texture`  : carries the selected `preset` (UI is 2-tier:
 *                `pen='texture'` + preset selector, design §10.1).
 * - `tone`     : screentone parameters for `pen='tone'` (texture-brush-design.md
 *                §12.2 / consult 2026-06-20 段階1). `pattern` は段階1 では `'dot'`
 *                (網点) のみ、`density` は 0..1 の連続スカラ番手 (= ディザ閾値の
 *                ベース). グラデ (= 軸ランプ) は段階2 でこの `density` を構造化
 *                拡張する予定だが、段階1 ではスカラのまま. 閾値ディザ kernel は
 *                drawing-engine 担当. `pitch` (phase11-light-pens-2026-08-
 *                21.md §4.5/§4.6) はドット間隔 (world px、256 の約数、seamless
 *                tiling 制約) — optional、欠落時は render 側で旧固定値 4 に
 *                フォールバック (= 過去に記録されたトーンストロークの replay
 *                忠実性を保つ). UI は露出時、常に明示 `pitch` を載せる想定
 *                (既定 16 以上、4/8 は柄として成立しないことが実機確認済み).
 * - `airbrush` : parameters for `pen='airbrush'` (airbrush-design-2026-08-15.md
 *                §1/§4). All three fields are consumed by the stateless hash
 *                `hash(seed, pointIndex, i, nx>>grain, ny>>grain)` that feeds
 *                the threshold seam shared with `tone` (design §2.2):
 *                - `seed`    : per-stroke u32, client-generated
 *                              (`crypto.getRandomValues`), never regenerated by
 *                              the server (design §4-4 / §5-3) — otherwise the
 *                              sender and peers would diverge. Closed to u32
 *                              range (`.max(0xFFFFFFFF)`) so the JS (`>>> 0`)
 *                              and Rust (`as u32`) truncations can't disagree
 *                              on out-of-range input (design §4-2).
 *                - `grain`   : cell-quantization shift (world coords, 0..3) —
 *                              grain size of the spray (design §1.3). Default
 *                              (1) lives on the render side when omitted.
 *                - `density` : spray density (0..1). Default lives on the
 *                              render side when omitted.
 *                `grain` / `density` are carried on the wire (not left as pure
 *                render-side constants) because they're intended to be
 *                UI-adjustable — any value that can vary per stroke must be
 *                replayable from session-buffer / snapshot / late-join
 *                history, which requires it to be part of the recorded event
 *                (design §4-2).
 *
 * - `crayon`   : parameters for `pen='crayon'` (phase11-light-pens-2026-08-21.md
 *                §2.6). `grain` selects the baked wax-grain map's coarseness
 *                (0..2, render side picks one of 3 pre-baked 256×256 world
 *                maps — no per-pixel kernel branch). Optional with a
 *                render-side default (mid coarseness) — unlike `airbrush`'s
 *                `seed`, there is no fairness/determinism reason a missing
 *                `grain` can't fall back, so a `crayon` stroke without
 *                `penParams` (or a mismatched arm) still renders safely.
 * - `pastel`   : parameters for `pen='pastel'` (same doc §2.6). `grain` picks
 *                one of 3 pre-baked fine-powder-grain world maps, same
 *                contract as `crayon`'s `grain`.
 * - `dot`      : parameters for `pen='dot'` (same doc §3.5). `spacing` is the
 *                gap between stamped dots in **sweep-step units** (=
 *                `steps = ceil(max(|dx|,|dy|))`, Chebyshev distance — NOT px;
 *                see the doc for why). Required (no valid stroke-level
 *                default the way texture's `paper` or tone's mid-番手 are —
 *                spacing is the pen's entire visual identity), but the
 *                render side still falls back to a fixed default spacing for
 *                a missing/mismatched `penParams` (malformed/legacy replay
 *                safety net, same spirit as `airbrush`'s reject-not-fallback
 *                EXCEPT spacing has no fairness concern so a default is
 *                safe — see `resolveStampRenderer`). `shape` is reserved for
 *                a future non-circle stamp shape (♡ / ★) and is UI-inert
 *                today (only `'circle'` is defined) — placed on the arm now,
 *                mirroring how `airbrush`'s `penParams` arm was reserved
 *                ahead of its own render implementation, so a future shape
 *                doesn't need a protocol change.
 *
 * `pen` ↔ `penParams.kind` consistency (e.g. `pen='texture'` must pair with the
 * `texture` arm, `pen='tone'` with the `tone` arm, `pen='airbrush'` with the
 * `airbrush` arm) is intentionally NOT cross-refined here — the render side
 * owns that, and the schema stays loose (texture-brush-design.md §4 / §11
 * step 1). For `airbrush` specifically, a missing/mismatched `penParams` has
 * no valid fallback (unlike texture's `paper` default) because a synthesized
 * seed would make every such stroke render identically for every sender
 * (airbrush-design-2026-08-15.md §4-3) — the receive path is expected to
 * reject rather than fall back; see `StrokeService.handleStart` in
 * `@oekaki/server`. `crayon` / `pastel` / `dot` all have safe render-side
 * defaults (phase11-light-pens-2026-08-21.md §2.6/§3.5), so — unlike
 * `airbrush` — **no server reject path is needed for these three**.
 */
export const penParamsSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('texture'), preset: texturePresetSchema }),
    z.object({
        kind: z.literal('tone'),
        pattern: z.enum(['dot']),
        density: z.number().min(0).max(1),
        // phase11-light-pens-2026-08-21.md §4.5/§4.6: dot-grid pitch (world px,
        // seamless-tiling divisor of 256). Optional so `oekaki-v1` stays intact;
        // omitted → the render side falls back to `TONE_DEFAULT_PITCH` (= the OLD
        // hardcoded cell size, 4) so already-recorded tone strokes that predate
        // this field keep replaying byte-identically. The UI is expected to
        // always send an explicit `16`/`32`/`64` once tone's chip is un-commented
        // (`4`/`8` render as noise, not a legible dot pattern — see the render
        // side's `TONE_PITCHES` doc for the full 実機 finding) — the schema
        // itself stays permissive (`toneThreshold.ts`'s `tonePitchIndex` is the
        // single belt-and-braces fallback point on the render side).
        pitch: z.number().int().min(1).optional(),
    }),
    z.object({
        kind: z.literal('airbrush'),
        seed: z.number().int().min(0).max(0xffffffff),
        grain: z.number().int().min(0).max(3).optional(),
        density: z.number().min(0).max(1).optional(),
    }),
    z.object({
        kind: z.literal('crayon'),
        grain: z.number().int().min(0).max(2).optional(),
    }),
    z.object({
        kind: z.literal('pastel'),
        grain: z.number().int().min(0).max(2).optional(),
    }),
    z.object({
        kind: z.literal('dot'),
        spacing: z.number().min(1).max(200),
        shape: z.enum(['circle']).optional(),
    }),
]);
/** Mask configuration on a stroke (none / normal / inverse, up to 2 colors). */
export const strokeMaskSchema = z.object({
    type: z.enum(['none', 'normal', 'inverse']),
    colors: z.array(z.number().int()).max(2),
});
// ─── client → server ────────────────────────────────────────────────────────
/**
 * `stroke.start` — emitted once at the beginning of a stroke. Carries
 * everything the receiver needs to set up the active stroke entry; no
 * points are sent here (protocol.md §4.2).
 */
export const strokeStartSchema = z.object({
    t: z.literal('stroke.start'),
    sid: z.uuid(),
    pen: strokePenSchema,
    /** RGB24 (0..0xFFFFFF). For `pen='eraser'` this field is ignored at render. */
    color: z.number().int().min(0).max(0xffffff),
    /** Stroke alpha in 0..255. */
    alpha: z.number().int().min(0).max(255),
    width: z.number().min(MIN_STROKE_WIDTH).max(MAX_STROKE_WIDTH),
    /** Layer to draw on. MVP omits or sends 0. (protocol.md §4.5) */
    layerId: z.number().int().min(0).default(0),
    mask: strokeMaskSchema.optional(),
    /**
     * Pen-specific parameters (texture-brush-design.md §4 案B / §11 step 1).
     * Optional — `pen='brush'/'flat'/'eraser'` omit it (back-compat, oekaki-v1
     * preserved: this is an optional-field addition only). When `pen='texture'`
     * and `penParams` is omitted, the render side falls back to the `paper`
     * default (spike behaviour). `pen` ↔ `penParams.kind` consistency is handled
     * at render time, so no cross-refine is applied here.
     */
    penParams: penParamsSchema.optional(),
    /**
     * Anti-aliasing flag (consult 2026-06-08 / modern-layout.md §9.9). When
     * `true`, drawing-core rasterizes the stroke through the analytic SDF
     * coverage path (`stampOnceAA`, Modern UI); when `false`/absent it uses the
     * legacy binary-stamp path (Classic, pixel-exact alias). Per-stroke so
     * Classic / Modern users coexist in one room and every snapshot replays
     * each stroke with its own flag — deterministically, since client and
     * server run the identical drawing-core TS (the flag only selects a code
     * path, no float divergence). Absent ⇒ `false` keeps existing recorded
     * strokes byte-identical (= back-compat with pre-AA events.bin / buffers).
     * Honoured for additive pens only; `eraser` ignores it at render time.
     */
    aa: z.boolean().default(false),
    /**
     * Input device that started this stroke (`PointerEvent.pointerType`;
     * pen-usage-analytics-2026-08-18.md §12). **Analytics only**: the server
     * counts it into `session.end` and STRIPS it before broadcast / session
     * buffer / stroke log (so it never reaches other participants nor
     * recordings). Optional ⇒ oekaki-v1 preserved. `.catch(undefined)` so an
     * unknown value from a future client never rejects the stroke itself.
     */
    input: z.enum(['pen', 'touch', 'mouse']).optional().catch(undefined),
    /**
     * `true` when the pen-pressure setting (width or alpha axis) was ON for
     * this stroke's pen (§12.3). Analytics only; stripped like `input`.
     * `.catch(undefined)` so a malformed value can never drop a stroke.
     */
    pressure: z.boolean().optional().catch(undefined),
});
/**
 * `stroke.segment` — a batch of points appended to the active stroke.
 * Sent on every `requestAnimationFrame` while the pointer is down.
 */
export const strokeSegmentSchema = z.object({
    t: z.literal('stroke.segment'),
    sid: z.uuid(),
    pts: z.array(strokePointSchema).min(1).max(MAX_SEGMENT_POINTS),
});
/** `stroke.end` — pointerup terminator. */
export const strokeEndSchema = z.object({
    t: z.literal('stroke.end'),
    sid: z.uuid(),
});
// ─── server → client (broadcast) ────────────────────────────────────────────
//
// Server-stamped fields (protocol-events.md §0.1): `uid` / `seq` / `ts`.
// We `.extend()` the client-side schema so the discriminator literal stays
// identical and the field set is a strict superset.
//
// `stroke.start` additionally carries a server-stamped `displayName`
// (Phase 6.9): the sender's `member.displayName` at broadcast time, captured
// once per stroke. This serves two roles (implementation-plan.md §Phase 6.9 /
// protocol-events.md §1):
//   1. drawing engine NameLabel — the receiver can build a label
//      `{ x, y, name=displayName, uid, seq }` immediately on `stroke.start`,
//      and the label survives any subsequent `member-left` / rename because
//      it's frozen into the broadcast payload.
//   2. events.bin replay (Phase 14+) — the 30-day events.bin keeps the
//      stamped broadcast verbatim, so a future "draw log" feature can
//      reconstruct displayName for every historical stroke without consulting
//      a separate name-history table.
//
// Following the `chat.message` precedent (protocol-events.md §3.4), the
// client-side `strokeStartSchema` does NOT carry `displayName`; only the
// broadcast schema does. Anything the client sends in that field would be
// overwritten by the server stamp.
const stamped = {
    uid: z.string(),
    seq: z.number().int().nonnegative(),
    ts: z.number().int().nonnegative(),
};
export const strokeStartBroadcastSchema = strokeStartSchema
    .omit({ input: true, pressure: true })
    .extend({
    ...stamped,
    /**
     * Sender's `member.displayName` captured at broadcast time. Required on
     * the broadcast — server stamps unconditionally (Phase 6.9). Range
     * matches `MIN/MAX_DISPLAY_NAME_LENGTH` (= same constraint room.join /
     * member.* events enforce, so a name that survived intake here is always
     * within range).
     */
    displayName: z.string().min(MIN_DISPLAY_NAME_LENGTH).max(MAX_DISPLAY_NAME_LENGTH),
});
export const strokeSegmentBroadcastSchema = strokeSegmentSchema.extend(stamped);
export const strokeEndBroadcastSchema = strokeEndSchema.extend(stamped);
// ─── stroke.cancel — conflict-aware Undo (β, 2026-07-22 確定) ───────────────
//
// (protocol-events.md §1.1 / docs/_wip/undo-conflict-aware-2026-07-12.md
// 「設計確定」節が正)
//
// `stroke.cancel` (c→s) requests cancellation of the sender's own stroke.
// The server validates 6 steps (owner / not too-old / ended / not locked by
// an in-flight snapshot job / no bbox-intersecting later stroke on the same
// layer — bbox is SERVER-authoritative, client-sent geometry is never
// trusted) and either broadcasts `stroke.cancelled` or unicasts
// `stroke.cancel-rejected`.
//
// `stroke.cancelled` is a server-stamped DRAWING event (tombstone): it is
// appended to the session buffer and the stroke log with its own `seq`, and
// is NOT a transient notification. Resume deltas and the timelapse live
// tail therefore replay it naturally; receivers apply a bbox-local rewind.
// Receivers MUST ignore a cancel for an unknown sid (idempotent no-op —
// late joiners never see the cancelled stroke because welcome deltas are
// pair-filtered server-side).
/** `stroke.cancel` — cancel own stroke (LIFO, most recent ended stroke first). */
export const strokeCancelSchema = z.object({
    t: z.literal('stroke.cancel'),
    sid: z.uuid(),
});
/**
 * `stroke.cancelled` — broadcast tombstone (server-stamped, enters the
 * session buffer + stroke log). `layerId` is stamped by the server from the
 * cancelled stroke's own metadata so receivers can rewind without a lookup.
 */
export const strokeCancelledBroadcastSchema = z.object({
    t: z.literal('stroke.cancelled'),
    sid: z.uuid(),
    layerId: z.number().int().min(0),
    ...stamped,
});
/**
 * Rejection reasons (protocol-events.md §1.1 の 6 ステップ判定 + rate limit):
 * - `not-owner`     : the stroke belongs to another user
 * - `too-old`       : not in the session buffer, or `startSeq <= 最終 publish 済み
 *                     snapshotSeq` (跨ぎ stroke — its partial pixels are already
 *                     baked into the snapshot PNG, 2026-07-22 検証)
 * - `not-ended`     : the stroke is still in flight (no `stroke.end` observed)
 * - `locked`        : a snapshot job is running and `startSeq <= snapshotJobCutoffSeq`
 *                     (strokes newer than the dispatch cutoff stay cancellable)
 * - `conflict`      : a later stroke bbox-intersects on the same layer — own or
 *                     another user's (own-later would self-orphan the upper
 *                     stroke), including the growing bbox of in-flight strokes
 * - `rate-limited`  : per-connection `stroke.cancel` throughput cap exceeded
 *                     (undo-conflict-aware-2026-07-12.md 「着手前チェックリスト」
 *                     rate limit 項。checked BEFORE the 6-step validity check —
 *                     a spamming client never reaches the buffer lookup)
 */
export const strokeCancelRejectReasonSchema = z.enum([
    'not-owner',
    'too-old',
    'not-ended',
    'locked',
    'conflict',
    'rate-limited',
]);
/**
 * `stroke.cancel-rejected` — unicast response to the requester only. Not a
 * broadcast, carries no server stamps (mirrors `whiteboard.imagepaste-rejected`).
 *
 * `conflicts` is present only for `reason='conflict'`: the intersecting
 * strokes' identities for the rejection-highlight UI. Capped at 20 entries
 * (拒否 UI のハイライト用途には十分 — entry-info の participant cap と同判断)。
 *
 * `retryAfterMs` is present only for `reason='rate-limited'`: remaining
 * cooldown in ms until the fixed window resets, mirroring
 * `whiteboard.imagepaste-rejected.retryAfterMs` exactly (same field name /
 * semantics, same "only meaningful for this one reason" convention).
 */
export const strokeCancelRejectedSchema = z.object({
    t: z.literal('stroke.cancel-rejected'),
    sid: z.uuid(),
    reason: strokeCancelRejectReasonSchema,
    conflicts: z
        .array(z.object({ sid: z.uuid(), uid: z.string() }))
        .max(20)
        .optional(),
    retryAfterMs: z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=stroke.js.map